import type { User } from "@assetwatch/auth";
import { z } from "zod";
import { mlApiErrorStatus, postMlApi } from "../../api/mlApi";
import config from "../../config";
import {
  assertCanAccessMlFrontend,
  type MlFrontendAccessFlags,
} from "../../lib/access";

/**
 * The Hub Health page's own ML API endpoint, `POST /v1/frontend/hub_health`.
 * Two meths: `list_facilities` (every facility's summary, worst first, plus the
 * fleet meta) and `get_facility` (one facility's summary and all its hubs).
 *
 * The shapes follow ml-infra's `hub_health_handoff/hub_health_api_contract.html`
 * as the backend actually returns them (`frontend_function_code/hub_health/
 * hub_health.py`).
 *
 * Null is "not measured", never zero, everywhere in this payload — so every
 * number the backend can't always supply is nullable here, and the UI renders
 * it as missing.
 */

const nullableNumber = z.number().nullable();

/** One of the six scoring factors. `utilisation` is British in the payload. */
export const FACTOR_KEYS = [
  "timeliness",
  "utilisation",
  "delivery",
  "redundancy",
  "placement",
  "schedule",
] as const;

export type FactorKey = (typeof FACTOR_KEYS)[number];

/** Help text for one factor, generated in Python. Rendered verbatim. */
const definitionSchema = z.object({
  title: z.string(),
  measures: z.string(),
  input: z.string(),
  scale: z.string(),
  means: z.string(),
});

export type FactorDefinition = z.infer<typeof definitionSchema>;

/**
 * Every scale marker the UI draws. `miss_rate_zero` and `schedule_zero` are
 * percents (25, 40), unlike `facts.miss_rate`, which is a 0–1 fraction.
 */
const thresholdsSchema = z.object({
  delay_good: z.number(),
  delay_zero: z.number(),
  miss_rate_zero: z.number(),
  red_min: z.number(),
  red_max: z.number(),
  rssi_good: z.number(),
  rssi_zero: z.number(),
  tx_good: z.number(),
  tx_zero: z.number(),
  capacity: z.number(),
  util_good: z.number(),
  schedule_zero: z.number(),
});

export type HubHealthThresholds = z.infer<typeof thresholdsSchema>;

/** Same cost shape the other pages' Athena endpoints report. */
const costSchema = z.object({
  usd: z.number().nullish().catch(null),
  mb_scanned: z.number().nullish().catch(null),
  rows: z.number().nullish().catch(null),
  seconds: z.number().nullish().catch(null),
});

export type HubHealthCost = z.infer<typeof costSchema>;

/** The fleet-wide fields both meths return. */
const metaShape = {
  /** The partition actually read. Send it back with `get_facility`. */
  date: z.string(),
  /** "9/16"-style, oldest first. Every series is aligned to these. */
  day_labels: z.array(z.string()),
  delay_unit: z.string(),
  /** Always null: informational only, no longer scored. */
  util_target: nullableNumber,
  weights: z.record(nullableNumber),
  thresholds: thresholdsSchema,
  definitions: z.record(definitionSchema),
  cost: costSchema.nullish().catch(null),
};

/**
 * A facility's summary, computed from its hubs (sensor-weighted). `factors` has
 * all six keys, but any can be null. Counts arrive as decimals ("33.0").
 */
const summarySchema = z.object({
  hubs: z.number(),
  sensors: z.number(),
  health: nullableNumber,
  /** excellent / good / fair / poor / failing / not scored. Rendered verbatim. */
  band: z.string(),
  worst_hub: nullableNumber,
  sole_served: z.number(),
  factors: z.record(nullableNumber),
});

export type FacilitySummary = z.infer<typeof summarySchema>;

/** One factor a hub was scored on. `why`, `measures` and `scale` are verbatim. */
const hubFactorSchema = z.object({
  key: z.string(),
  title: z.string(),
  score: nullableNumber,
  band: z.string(),
  weight: nullableNumber,
  why: z.string(),
  measures: z.string(),
  scale: z.string(),
});

export type HubFactor = z.infer<typeof hubFactorSchema>;

/**
 * Seven points per series, aligned to `day_labels`. A whole series is null when
 * the pipeline didn't store it; a point is null when that day has no data —
 * `missed` and `expected` are always null on the last day.
 */
const seriesValues = z.array(nullableNumber).nullable();

const hubSeriesSchema = z.object({
  delay: seriesValues,
  sensors: seriesValues,
  readings: seriesValues,
  missed: seriesValues,
  expected: seriesValues,
  scheduled: seriesValues,
  tx: seriesValues,
});

export type HubSeries = z.infer<typeof hubSeriesSchema>;
export type HubSeriesKey = keyof HubSeries;

/**
 * The inputs behind a hub's scores. All nullable: a hub with no BLE diagnostic
 * has no `in_range` or RSSI. `expected` and `missed` are genuinely fractional
 * (a miss is shared between hubs) — round for display only.
 */
const hubFactsSchema = z.object({
  sensors: nullableNumber,
  readings: nullableNumber,
  scheduled_per_day: nullableNumber,
  capacity_share: nullableNumber,
  avg_delay: nullableNumber,
  expected: nullableNumber,
  missed: nullableNumber,
  miss_rate: nullableNumber,
  sole_served: nullableNumber,
  over_covered: nullableNumber,
  in_range: nullableNumber,
  avg_rssi: nullableNumber,
  avg_rssi_in_range: nullableNumber,
  tx_mean: nullableNumber,
  collisions: nullableNumber,
});

export type HubFacts = z.infer<typeof hubFactsSchema>;

const hubSchema = z.object({
  hub: z.string(),
  health: nullableNumber,
  band: z.string(),
  /** Sparse: only the factors this hub could be scored on, in payload order. */
  factors: z.array(hubFactorSchema),
  series: hubSeriesSchema,
  facts: hubFactsSchema,
});

export type Hub = z.infer<typeof hubSchema>;

export const listFacilitiesResponseSchema = z.object({
  ...metaShape,
  total_facilities: z.number(),
  /** Hubs scored but in no facility, so fleet totals reconcile. */
  unassigned_hubs: z.number(),
  page: z.number(),
  page_size: z.number(),
  pages: z.number(),
  /** Worst health first. No hubs here — those come from `get_facility`. */
  facilities: z.array(z.object({ id: z.string(), summary: summarySchema })),
});

export type ListFacilitiesResponse = z.infer<
  typeof listFacilitiesResponseSchema
>;
export type FacilityListItem = ListFacilitiesResponse["facilities"][number];

export const getFacilityResponseSchema = z.object({
  ...metaShape,
  facility: z.object({
    id: z.string(),
    summary: summarySchema,
    /** Worst health first. */
    hubs: z.array(hubSchema),
    /** Always 0 for now: every hub is returned. */
    hidden_hubs: z.number(),
    /** Always [] while machines are deferred; the page doesn't render it. */
    machines: z.array(z.unknown()),
  }),
});

export type GetFacilityResponse = z.infer<typeof getFacilityResponseSchema>;
export type FacilityDetail = GetFacilityResponse["facility"];

const HUB_HEALTH_PATH = `${config.mlApi.frontendPath}/hub_health`;

/**
 * The backend's ceiling. The fleet is ~1,650 facilities, so one call at this
 * size is the whole fleet (~0.5 MB) — enough for a local search and a
 * worst-first list without paging the API.
 */
const FLEET_PAGE_SIZE = 2000;

type AccessInput = {
  user: User | null;
  flags: MlFrontendAccessFlags;
};

/**
 * Every facility's summary, worst health first, from the newest partition in
 * the last 7 days. One Athena scan (~3.5s). Keep the response's `date`: it's
 * what `get_facility` must be sent so the detail matches this list.
 */
export async function fetchHubHealthFacilities({
  user,
  flags,
}: AccessInput): Promise<ListFacilitiesResponse> {
  // Defense-in-depth: the route is flag- and role-gated upstream, and the
  // endpoint enforces IAM and its allowlist, but the check runs before signing.
  assertCanAccessMlFrontend({ flags, user });

  const payload = await postMlApi(HUB_HEALTH_PATH, {
    meth: "list_facilities",
    page_size: FLEET_PAGE_SIZE,
  });

  return listFacilitiesResponseSchema.parse(payload);
}

/**
 * One facility's summary and every hub, worst first. One Athena scan (~2s).
 *
 * `date` should be the one `list_facilities` returned, so the detail shows the
 * same snapshot as the list it was opened from. Resolves to null on the
 * backend's 404 — the facility has no scored hubs on that date — because that
 * is an answer to show, not a failure.
 */
export async function fetchHubHealthFacility({
  user,
  flags,
  facilityId,
  date,
}: AccessInput & {
  facilityId: string;
  date: string;
}): Promise<GetFacilityResponse | null> {
  assertCanAccessMlFrontend({ flags, user });

  let payload: unknown;
  try {
    payload = await postMlApi(HUB_HEALTH_PATH, {
      meth: "get_facility",
      external_facility_id: facilityId,
      date,
    });
  } catch (error) {
    if (mlApiErrorStatus(error) === 404) {
      return null;
    }
    throw error;
  }

  return getFacilityResponseSchema.parse(payload);
}
